import { ChangeDetectionStrategy, Component, ErrorHandler, Injector, OnDestroy, OnInit, ViewChild, inject, input, output, signal } from '@angular/core'
import { FieldTree } from '@angular/forms/signals'

import { MatOptionSelectionChange } from '@angular/material/core'
import { MatInput } from '@angular/material/input'

import { Lasku, LaskuBase, LaskunAsiakas, LaskunAsiakastyyppi, LaskunTyypit, LaskunTyyppi, TypeaheadAsiakas } from '@lemon/core-firebase/model/lasku'

import { BehaviorSubject, combineLatest, Observable, ReplaySubject, Subject } from 'rxjs'
import { distinctUntilChanged, takeUntil } from 'rxjs/operators'

import { LaskuKopioija } from '@lemon/core-firebase/service/lasku/lasku.kopioija'
import { StringService } from '@lemon/core/service/string.service'
import { arvonMuutokset, asetaArvo, nollaa } from '../_jaettu-angular/_lomakkeet/arvon-muutokset'
import { FormValidationService } from '../_jaettu-angular/service/form-validation.service'
import { LaskunAsiakasTypeaheadBase } from '../_jaettu-angular/service/lasku/typeahead.service'
import { LemonTranslationService } from '../_jaettu-angular/service/lemon-translation.service'
import { LaskuLomake } from './lasku-rivit.component'
import { LaskunAsiakkaanValidointiService, VatTaiYtunnuksePakollisuus } from './lasku-asiakkaan-validointi.service'

@Component({
  selector: 'app-lasku-asiakas',
  templateUrl: './lasku-asiakas.component.html',
  styleUrls: ['./lasku-asiakas.component.css'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class LaskuAsiakasComponent implements OnInit, OnDestroy {
  @ViewChild('asiakassearch', { read: MatInput }) materialInput: MatInput

  readonly laskunTyyppiObservable = input<Observable<LaskunTyyppi>>()
  readonly laskuObservable = input<Observable<{ juurilasku: Lasku; kasiteltava: LaskuBase }>>()
  readonly parent = input<FieldTree<LaskuLomake>>()
  readonly paivitaValidaatiot = input<unknown>()

  readonly asiakasMuuttui = output<LaskunAsiakas>()
  readonly uusiAsiakas = output<string>()
  readonly muokkaa = output<void>()

  // eslint-disable-next-line @typescript-eslint/naming-convention
  public LaskunTyypit = LaskunTyypit

  onkoYtunnusPakollinen: VatTaiYtunnuksePakollisuus = 'ei-pakollinen'
  suodatetutAsiakkaat: ReplaySubject<TypeaheadAsiakas[]> = new ReplaySubject<TypeaheadAsiakas[]>(1)
  asiakashaunVihjetekstiObservable: BehaviorSubject<string> = new BehaviorSubject('')
  valittuAsiakas = false
  readonly naytaCheck = signal(false)
  readonly naytaKortti = signal(false)
  readonly asiakasikoni = signal('business')
  readonly viimeisinHakuteksti = signal('')

  private ngUnsubscribe = new Subject<void>()
  private ngUnsubscribeKomponentti = new Subject<void>()
  private readonly _injector = inject(Injector)

  constructor(
    private _errorHandler: ErrorHandler,
    private _validationService: FormValidationService,
    private _laskunAsiakasTypeahead: LaskunAsiakasTypeaheadBase,
    private _laskuKopioija: LaskuKopioija,
    private _translationService: LemonTranslationService,
    private _stringService: StringService,
    private _laskunAsiakkaanValidointiService: LaskunAsiakkaanValidointiService,
  ) {}

  focus() {
    setTimeout(() => {
      if (this.materialInput) {
        this.materialInput.focus()
      }
    }, 50)
  }

  lisaaUusiAsiakas(event: MatOptionSelectionChange) {
    if (!event.isUserInput) {
      return
    }
    this.uusiAsiakas.emit(this.viimeisinHakuteksti())
  }

  naytaUudenAsiakkaanKortti() {
    this.naytaKortti.set(true)
  }

  get nimiTekstina(): string {
    const arvo = this.nimi?.().value()
    if (this._stringService.isString(arvo)) {
      return arvo
    }
    return this.displayFn(arvo as LaskunAsiakas)
  }

  get osoite(): string {
    const postiosoite = `${this.postinro?.().value() ?? ''} ${this.postitoimipaikka?.().value() ?? ''}`.trim()
    return [this.katuosoite?.().value(), postiosoite].filter(osa => !!osa).join(', ')
  }

  tarkistaPoisto() {
    if (!this.nimi().value()) {
      this.poistaValittu()
    }
  }

  poistaValittu() {
    this.tyhjennaAsiakashaunVihjeteksti()
    const s: LaskunAsiakas = {
      avain: null,
      nimi: '',
      ytunnus: '',
      katuosoite: '',
      postinro: '',
      postitmp: '',
      maa: this.parent().maa().value(),
      date: null,
      laskunVastaanottajat: [],
      laskunKieli: null,
      laskunTyyppi: null,
      laskunValuutta: null,
      sahkoinenosoite: {
        sahkoinenOsoite: null,
        sahkoinenValittaja: null,
      },
      asiakastyyppi: null,
      viimeisinLaskuLahetetty: null,
    }
    nollaa(this.ytunnus, '')
    nollaa(this.katuosoite, '')
    nollaa(this.nimi, null)
    nollaa(this.postinro, '')
    nollaa(this.postitoimipaikka, '')
    nollaa(this.maa, null)
    this.naytaKortti.set(false)
    this.viimeisinHakuteksti.set('')
    this.asiakasMuuttui.emit(s)
    this.focus()
  }

  ngOnDestroy() {
    this.ngUnsubscribeKomponentti.next()
    this.ngUnsubscribeKomponentti.complete()
    this.ngUnsubscribeKomponentti = new Subject<void>()
  }

  ngOnInit() {
    combineLatest([this.laskunTyyppiObservable(), arvonMuutokset(this.maa, { injector: this._injector }).pipe(distinctUntilChanged())])
      .pipe(takeUntil(this.ngUnsubscribeKomponentti))
      .subscribe(([tyyppi, maakoodi]) => {
        this.onkoYtunnusPakollinen = this._laskunAsiakkaanValidointiService.annaYtunnuksenPakollisuus(tyyppi, maakoodi)
        asetaArvo(this.parent().ytunnuksenPakollisuus, this.onkoYtunnusPakollinen)
      })

    this.laskuObservable()
      .pipe(takeUntil(this.ngUnsubscribeKomponentti))
      .subscribe(lasku => {
        this.ngUnsubscribe.next()
        this.ngUnsubscribe.complete()
        this.ngUnsubscribe = new Subject<void>()
        this.valittuAsiakas = !!lasku.kasiteltava.asiakas.avain
        this.naytaKortti.set(this.valittuAsiakas || !!lasku.kasiteltava.asiakas.katuosoite)
        this.asiakasikoni.set(lasku.kasiteltava.asiakas.asiakastyyppi === LaskunAsiakastyyppi.HENKILO ? 'person' : 'business')
        this._setupForm(lasku.kasiteltava)
        if (this.valittuAsiakas) {
          this.naytaCheck.set(true)
          this.asiakashaunVihjetekstiObservable.next(this._translationService.lokalisoi('lasku.tallennettu-asiakas'))
        } else {
          this.tyhjennaAsiakashaunVihjeteksti()
        }
      })
  }

  private _asetaTypeaheadAsiakas(typeaheadAsiakas: TypeaheadAsiakas) {
    if (!typeaheadAsiakas || !typeaheadAsiakas.avain) {
      return
    }
    asetaArvo(this.ytunnus, typeaheadAsiakas.ytunnus ?? '')
    this._laskunAsiakasTypeahead
      .annaAsiakas(typeaheadAsiakas)
      .then(asiakas => {
        if (asiakas) {
          this.tyhjennaAsiakashaunVihjeteksti()
          this.asiakasMuuttui.emit(this._laskuKopioija.copyAsiakas(asiakas))
          this.suodatetutAsiakkaat.next([])
        }
      })
      .catch(err => {
        this._errorHandler.handleError(err)
      })
  }

  private _setupForm(kasiteltava: LaskuBase) {
    if (kasiteltava && kasiteltava.asiakas) {
      const typeaheadAsiakas: TypeaheadAsiakas = {
        avain: kasiteltava.asiakas.avain,
        nimi: kasiteltava.asiakas.nimi,
        ytunnus: kasiteltava.asiakas.ytunnus,
      }

      asetaArvo(this.nimi, typeaheadAsiakas)
      asetaArvo(this.ytunnus, kasiteltava.asiakas.ytunnus ?? '')
      asetaArvo(this.katuosoite, kasiteltava.asiakas.katuosoite ?? '')
      asetaArvo(this.postinro, kasiteltava.asiakas.postinro ?? '')
      asetaArvo(this.postitoimipaikka, kasiteltava.asiakas.postitmp ?? '')
      asetaArvo(this.maa, kasiteltava.asiakas.maa ?? null)

      // Aseta kuuntelijat
      const muutokset = <T>(kentta: FieldTree<T>) => arvonMuutokset(kentta, { injector: this._injector }).pipe(takeUntil(this.ngUnsubscribe))
      muutokset(this.nimi).subscribe(value => {
        if (this._stringService.isString(value)) {
          kasiteltava.asiakas.nimi = value
          this.viimeisinHakuteksti.set(value.trim())
          this.asetaAsiakashaunVihjeteksti()
          if (this.valittuAsiakas) {
          } else {
            const kasitelty = this._validationService.processValue(value)
            this._laskunAsiakasTypeahead
              .haeJaSuodata(kasitelty)
              .then(result => {
                this.suodatetutAsiakkaat.next(result.suodatetut)
              })
              .catch(err => {
                this._errorHandler.handleError(err)
              })
          }
        } else if (value) {
          if (this.valittuAsiakas) {
            this.asetaAsiakashaunVihjeteksti()
            kasiteltava.asiakas.nimi = value.nimi
          } else {
            this._asetaTypeaheadAsiakas(value)
          }
        }
      })
      muutokset(this.ytunnus).subscribe(value => {
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.ytunnus !== processed) {
          kasiteltava.asiakas.ytunnus = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.katuosoite).subscribe(value => {
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.katuosoite !== processed) {
          kasiteltava.asiakas.katuosoite = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.postinro).subscribe(value => {
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.postinro !== processed) {
          kasiteltava.asiakas.postinro = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.postitoimipaikka).subscribe(value => {
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.postitmp !== processed) {
          kasiteltava.asiakas.postitmp = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })
      muutokset(this.maa).subscribe(value => {
        const processed = this._validationService.processValue(value)
        if (kasiteltava.asiakas.maa !== processed) {
          kasiteltava.asiakas.maa = processed
          this.asetaAsiakashaunVihjeteksti()
        }
      })

      // Aseta ytunnuksen pakollisuus
      asetaArvo(this.parent().ytunnuksenPakollisuus, this.onkoYtunnusPakollinen)
    }
  }

  private tyhjennaAsiakashaunVihjeteksti() {
    this.asiakashaunVihjetekstiObservable.next('')
  }

  private asetaAsiakashaunVihjeteksti() {
    if (this.valittuAsiakas) {
      this.asiakashaunVihjetekstiObservable.next(this._translationService.lokalisoi('lasku.muokkaa-asiakasta'))
    } else {
      this.asiakashaunVihjetekstiObservable.next(this._translationService.lokalisoi('lasku.tallenna-uutena-asiakkaana'))
    }
    this.naytaCheck.set(false)
  }

  displayFn(laskunAsiakas: LaskunAsiakas): string {
    if (laskunAsiakas && laskunAsiakas.nimi) {
      return laskunAsiakas.nimi
    }
    return ''
  }

  get katuosoite() {
    return this.parent()?.katuosoite
  }

  get nimi() {
    return this.parent()?.nimi
  }

  get ytunnus() {
    return this.parent()?.ytunnus
  }

  get postinro() {
    return this.parent()?.postinro
  }

  get postitoimipaikka() {
    return this.parent()?.postitoimipaikka
  }

  get maa() {
    return this.parent()?.maa
  }

  onkoVirhe<T>(kentta: FieldTree<T>, laji: string): boolean {
    return kentta()
      .errors()
      .some(virhe => virhe.kind === laji)
  }
}
